import { i18n } from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { procurementApi, type PurchaseOrderResponse } from '@/api/procurement.api';
import type { SelectOption } from '@/components/ui/Select';
import { formatPurchaseOrderLabel } from '@/utils/entityLabels';
import { useDebounce } from '@/hooks/useDebounce';
import { showApiError } from '@/utils/apiError';

function poOption(row: PurchaseOrderResponse): SelectOption {
  return { value: String(row.id), label: formatPurchaseOrderLabel(row) };
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

type Params = { value?: string; pageSize?: number; supplierId?: string };

export function usePurchaseOrderSelectSearch({ value = '', pageSize = 30, supplierId }: Params = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [rows, setRows] = useState<PurchaseOrderResponse[]>([]);
  const [selected, setSelected] = useState<PurchaseOrderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const load = useCallback(
    async (search: string) => {
      setSearching(true);
      try {
        const res = await procurementApi.listPurchaseOrders({
          search: search || undefined,
          page: 0,
          size: pageSize,
        });
        const content =
          supplierId != null && supplierId !== ''
            ? res.content.filter((r) => String(r.supplierId) === supplierId)
            : res.content;
        setRows(content);
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadPurchaseOrders', { ns: 'common' }));
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [pageSize, supplierId],
  );

  useEffect(() => {
    void load(debouncedSearch);
  }, [debouncedSearch, load]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const id = Number(value);
    if (Number.isNaN(id)) return;
    const fromList = rows.find((r) => r.id === id);
    if (fromList) {
      setSelected(fromList);
    }
  }, [value, rows]);

  const options = useMemo(() => {
    const base = rows.map(poOption);
    const sel = selected ? poOption(selected) : null;
    return mergeSelectedOption(base, sel);
  }, [rows, selected]);

  const byId = useMemo(() => {
    const map = new Map<number, PurchaseOrderResponse>();
    for (const r of rows) map.set(r.id, r);
    if (selected) map.set(selected.id, selected);
    return map;
  }, [rows, selected]);

  const getPurchaseOrder = useCallback(
    (id: number | string | null | undefined) => {
      if (id == null || id === '') return undefined;
      return byId.get(Number(id));
    },
    [byId],
  );

  return { options, loading, searching, onSearchQuery: setSearchQuery, getPurchaseOrder };
}